import type { Choice, Poll, PollResults } from "@vote/shared";
import type { PollFlow, PollFlowState } from "../core/poll-flow";

// Shadow DOMごとにIDの参照範囲が分かれるため、複数ウィジェットでも同じ値を安全に使える。
const RESULT_ANNOUNCEMENT_ID = "vote-result-announcement";
// 灰色の状態ピル。受付終了・開始前・初期集計中で同じ見た目にする。
const STATUS_PILL_CLASS_NAME =
  "rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-bold text-gray-600";

/** 回答IDに対応する選択肢名を返し、壊れた応答では空欄ではなく不整合を明示する。 */
export function getChoiceLabel(poll: Poll, choiceId: string): string {
  return poll.choices.find((choice) => choice.id === choiceId)?.label ?? "回答内容不明";
}

/**
 * サーバが全選択肢を0%込みで返す割合から1件を読む（T-0016。SDKは割合を計算しない）。
 * サーバは全選択肢を0票込みで数えてから割合にするため、本番経路では欠けない。SDKは成功応答を
 * 実行時検証しないので、契約違反の応答が届いた場合の保険として例外にする（0%で補完すると誤った
 * 結果を見せてしまう。resolve・投票の応答で起きた場合は PollFlow の catch でエラーカードになる）。
 */
export function getChoicePercentage(percentages: Record<string, number>, choiceId: string): number {
  const percent = percentages[choiceId];
  if (percent === undefined) {
    throw new Error(`集計結果に選択肢 ${choiceId} の割合がありません`);
  }
  return percent;
}

/** textContentを唯一の文字列挿入経路にし、API・ホスト由来文字列のXSSを防ぐ。 */
export function createElement<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className = "",
  text = "",
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (className !== "") {
    element.className = className;
  }
  element.textContent = text;
  return element;
}

/** SVG要素を名前空間付きで作り、デザイン原本の属性を文字列として安全に設定する。 */
export function createSvgElement<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attributes: Record<string, string>,
): SVGElementTagNameMap[K] {
  const element = document.createElementNS("http://www.w3.org/2000/svg", tag);
  for (const [name, value] of Object.entries(attributes)) {
    element.setAttribute(name, value);
  }
  return element;
}

/** 線画アイコンに共通するSVG属性を一度だけ定義し、各アイコンは固有パスだけを持つ。 */
export function createStrokeIcon(
  size: number,
  strokeWidth: string,
  className: string,
): SVGSVGElement {
  return createSvgElement("svg", {
    width: String(size),
    height: String(size),
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    "stroke-width": strokeWidth,
    "stroke-linecap": "round",
    "stroke-linejoin": "round",
    class: className,
    "aria-hidden": "true",
  });
}

/** Voteブランドを示す、角丸四角とチェックのロゴを生成する。 */
function createLogoIcon(): SVGSVGElement {
  const icon = createStrokeIcon(18, "2.2", "shrink-0 text-pink-600");
  icon.append(
    createSvgElement("rect", { x: "3", y: "3", width: "18", height: "18", rx: "4" }),
    createSvgElement("path", { d: "M8 12.5l2.8 2.8L16 9" }),
  );
  return icon;
}

/** ブランド名と文脈ラベルの関係を示す右向きシェブロンを生成する。 */
function createChevronIcon(): SVGSVGElement {
  const icon = createStrokeIcon(12, "2.5", "shrink-0 text-gray-400");
  icon.append(createSvgElement("path", { d: "M9 6l6 6-6 6" }));
  return icon;
}

/** 回答者数の前に置く人物グループアイコンを生成する。 */
function createPeopleIcon(): SVGSVGElement {
  const icon = createStrokeIcon(14, "2", "shrink-0");
  icon.append(
    createSvgElement("path", { d: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" }),
    createSvgElement("circle", { cx: "9", cy: "7", r: "4" }),
    createSvgElement("path", { d: "M23 21v-2a4 4 0 0 0-3-3.87" }),
    createSvgElement("path", { d: "M16 3.13a4 4 0 0 1 0 7.75" }),
  );
  return icon;
}

/** 回答済みバッジ内のチェックアイコンを生成する。 */
function createAnswerCheckIcon(): SVGSVGElement {
  const icon = createStrokeIcon(11, "3", "shrink-0");
  icon.append(createSvgElement("path", { d: "M5 13l4 4L19 7" }));
  return icon;
}

/** 選択状態に応じ、デザイン原本と同じ外円と内円を持つラジオ表示を生成する。 */
function createRadioIcon(selected: boolean): SVGSVGElement {
  const icon = createSvgElement("svg", {
    width: "20",
    height: "20",
    viewBox: "0 0 20 20",
    fill: "none",
    class: `pointer-events-none ${selected ? "text-pink-600" : "text-gray-400"}`,
    "aria-hidden": "true",
  });
  icon.append(
    createSvgElement("circle", {
      cx: "10",
      cy: "10",
      r: "8.25",
      stroke: "currentColor",
      "stroke-width": "1.5",
    }),
  );
  if (selected) {
    icon.append(createSvgElement("circle", { cx: "10", cy: "10", r: "4.5", fill: "currentColor" }));
  }
  return icon;
}

/** 通信失敗を示す、丸囲みの感嘆符アイコンを生成する。 */
function createErrorIcon(): SVGSVGElement {
  const icon = createStrokeIcon(28, "1.8", "text-gray-400");
  icon.append(
    createSvgElement("circle", { cx: "12", cy: "12", r: "10" }),
    createSvgElement("path", { d: "M12 8v4" }),
    createSvgElement("path", { d: "M12 16h.01" }),
  );
  return icon;
}

/** 既存マウントを壊さずShadow DOMを作り、コンパイル済みCSSを一度だけ注入する。 */
export function createShadowRoot(container: HTMLElement, widgetStyles: string): ShadowRoot | null {
  if (container.shadowRoot !== null) {
    return null;
  }
  const shadow = container.attachShadow({ mode: "open" });
  const style = createElement("style");
  style.textContent = widgetStyles;
  shadow.append(style);
  return shadow;
}

/** 全状態で共通する白い投票カードを生成する。 */
function createCard(): HTMLElement {
  return createElement(
    "article",
    "flex flex-col gap-3.5 rounded-xl border border-gray-200 bg-white p-5 font-sans text-gray-900 shadow-[0_1px_2px_rgba(17,24,39,0.05)]",
  );
}

/** 操作後のフォーカス先を作り、必要なら画面外の状態説明も読み上げ対象へ関連付ける。 */
function createFocusHeading(
  text: string,
  className: string,
  descriptionId?: string,
): HTMLHeadingElement {
  const heading = createElement("h2", className, text);
  heading.tabIndex = -1;
  heading.dataset.voteFocusTarget = "";
  if (descriptionId !== undefined) {
    heading.setAttribute("aria-describedby", descriptionId);
  }
  return heading;
}

/** Voteブランドを表示し、設問がある状態ではカードの文脈ラベルまで案内する。 */
function createHeader(showQuestionContext = true): HTMLElement {
  const header = createElement("header", "flex items-center gap-1.5");
  header.append(createLogoIcon(), createElement("span", "text-sm font-bold text-pink-600", "Vote"));
  if (showQuestionContext) {
    header.append(
      createChevronIcon(),
      createElement("span", "text-[13px] font-semibold text-gray-700", "あなたならどうする？"),
    );
  }
  return header;
}

/** 人物アイコンと桁区切り済み総数を一組にし、人数を出す full の全状態で回答者数表記を統一する。 */
function createResponseCount(total: number): HTMLElement {
  const count = createElement("div", "flex items-center gap-1.5 text-xs text-gray-500");
  // 固定の日本語文とデザイン原本のカンマ表記が、閲覧者のブラウザロケールで崩れないようにする。
  const formattedTotal = total.toLocaleString("ja-JP");
  count.append(createPeopleIcon(), createElement("span", "", `${formattedTotal}人が回答`));
  return count;
}

/** 回答後の状態を、チェック付きの小さなバッジとして表示する。 */
function createAnsweredBadge(): HTMLElement {
  const badge = createElement(
    "span",
    "inline-flex items-center gap-1 rounded-full bg-pink-50 px-2.5 py-1 text-[11px] font-bold text-pink-600",
  );
  badge.append(createAnswerCheckIcon(), createElement("span", "", "回答済み"));
  return badge;
}

/** API設問をカードの主見出しとして表示し、終了画面では操作後のフォーカス先にもする。 */
function createQuestion(poll: Poll, descriptionId?: string): HTMLHeadingElement {
  const heading = createElement(
    "h2",
    "text-base font-bold leading-[1.6] text-gray-900",
    poll.question,
  );
  if (descriptionId !== undefined) {
    heading.tabIndex = -1;
    heading.dataset.voteFocusTarget = "";
    heading.setAttribute("aria-describedby", descriptionId);
  }
  return heading;
}

/** SVGで見た目を合わせつつ、選択・矢印移動・読み上げはネイティブラジオへ委ねる。 */
function createChoiceLabel(
  choice: Choice,
  selectedChoiceId: string | null,
  interaction: "enabled" | "submitting" | "scheduled",
  flow: PollFlow,
): HTMLLabelElement {
  const selected = choice.id === selectedChoiceId;
  const disabled = interaction !== "enabled";
  // 開始前の灰色は既存の白背景を後から打ち消さず、選択肢の状態から直接決める。
  const appearance = selected
    ? "border-pink-600 bg-pink-50"
    : interaction === "scheduled"
      ? "border-gray-300 bg-gray-50"
      : "border-gray-300 bg-white hover:border-gray-400";
  const cursorStyle =
    interaction === "submitting"
      ? "cursor-wait opacity-70"
      : interaction === "scheduled"
        ? "cursor-not-allowed opacity-70"
        : "cursor-pointer";
  const label = createElement(
    "label",
    ["flex items-center gap-2.5 rounded-[10px] border px-3.5 py-3", appearance, cursorStyle].join(
      " ",
    ),
  );
  const radioControl = createElement("span", "relative h-5 w-5 shrink-0");
  const input = createElement(
    "input",
    `absolute inset-0 z-10 h-5 w-5 cursor-pointer appearance-none rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600 ${interaction === "scheduled" ? "disabled:cursor-not-allowed" : "disabled:cursor-wait"}`,
  );
  input.type = "radio";
  input.name = "vote-choice";
  input.value = choice.id;
  input.checked = selected;
  input.disabled = disabled;
  input.addEventListener("change", () => flow.selectChoice(choice.id));
  // SVGは装飾に限定し、フォーム値・フォーカス・支援技術向けの名前はinputに一本化する。
  radioControl.append(input, createRadioIcon(selected));
  label.append(
    radioControl,
    createElement(
      "span",
      `text-sm leading-5 ${selected ? "font-semibold text-gray-900" : interaction === "scheduled" ? "font-normal text-gray-500" : "font-normal text-gray-700"}`,
      choice.label,
    ),
  );
  return label;
}

/** 未回答・送信中を同じフォーム構造で描き、送信中だけ操作を無効化する。 */
function renderSelection(
  state: Extract<PollFlowState, { kind: "select" | "submitting" }>,
  flow: PollFlow,
): HTMLElement {
  const card = createCard();
  card.append(createHeader(), createQuestion(state.poll));

  const form = createElement("form", "flex flex-col gap-3.5");
  const disabled = state.kind === "submitting";
  card.setAttribute("aria-busy", String(disabled));
  const choices = createElement("fieldset", "flex min-w-0 flex-col gap-2.5");
  choices.append(createElement("legend", "sr-only", "回答を1つ選択"));
  for (const choice of state.poll.choices) {
    choices.append(
      createChoiceLabel(
        choice,
        state.selectedChoiceId,
        state.kind === "submitting" ? "submitting" : "enabled",
        flow,
      ),
    );
  }

  const button = createElement(
    "button",
    "flex h-[46px] w-full items-center justify-center rounded-full bg-pink-600 px-5 text-[15px] font-bold text-white hover:bg-pink-700 disabled:cursor-not-allowed disabled:bg-pink-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600",
    disabled ? "送信中…" : "回答する",
  );
  button.type = "submit";
  button.disabled = disabled || state.selectedChoiceId === null;
  const footer = createElement("div", "flex items-center justify-between");
  // 人数は full のときだけ出す。集計中は回答しても結果が出ないため、見えるものを正直に伝える。
  if (state.results.kind === "full") {
    footer.append(createResponseCount(state.results.total));
  }
  footer.append(
    createElement(
      "span",
      "text-xs text-gray-400",
      state.results.kind === "collecting"
        ? "初期集計中です。回答すると、あなたの回答が表示されます"
        : "回答すると結果が表示されます",
    ),
  );
  form.append(createElement("p", "text-xs text-gray-500", "（1つ選択）"), choices, button, footer);

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    void flow.submitVote();
  });
  card.append(form);
  return card;
}

/**
 * 結果バーの内容を、画面を見ない利用者にも同じ情報量で伝わる読み上げ文へ変換する。
 * 人数は画面に出す full のときだけ読み上げ、percentages では割合だけを伝える。
 */
function describeResults(
  poll: Poll,
  results: Exclude<PollResults, { kind: "collecting" }>,
  myChoiceId: string | null,
): string {
  const choiceSummaries = poll.choices.map((choice) => {
    const mine = choice.id === myChoiceId ? "、あなたの回答" : "";
    return `${choice.label}、${getChoicePercentage(results.percentages, choice.id)}パーセント${mine}`;
  });
  const responseCount = results.kind === "full" ? `全${results.total}人が回答しています。` : "";
  return `${choiceSummaries.join("。")}。${responseCount}`;
}

/** 回答済みカードの読み上げ。集計中は画面と同じく本人の回答だけを伝える。 */
function describeAnsweredResults(poll: Poll, results: PollResults, myChoiceId: string): string {
  return results.kind === "collecting"
    ? `初期集計中です。あなたの回答は${getChoiceLabel(poll, myChoiceId)}です。`
    : describeResults(poll, results, myChoiceId);
}

/** 永続live regionへ流す文言を状態遷移から作り、結果の新規DOM挿入に依存しない。 */
export function getStateAnnouncement(
  state: PollFlowState,
  previousKind?: PollFlowState["kind"],
): string {
  switch (state.kind) {
    case "submitting":
      return "回答を送信しています。";
    case "results": {
      const introduction =
        previousKind === "submitting" ? "回答を送信しました。" : "回答結果を表示しました。";
      const periodNotice = state.poll.availability === "period_ended" ? "投票期間終了。" : "";
      return `${introduction}${periodNotice}${describeAnsweredResults(state.poll, state.results, state.myChoiceId)}`;
    }
    case "scheduled":
      return "投票期間開始前。";
    case "rejected":
      return "受付期間外です。";
    case "closed":
      return `${state.poll.availability === "period_ended" ? "投票期間終了" : "この投票は受付を終了しました"}。${describeResults(state.poll, state.results, null)}`;
    case "loading":
    case "select":
    case "error":
      return "";
  }
}

/**
 * 選択肢ごとの割合を横棒で表示し、回答IDがあれば本人の回答を強調する。
 * 割合が null（初期集計中）のときは、選択肢名と本人の回答ピルだけを出す。
 */
function createResultRow(
  choice: Choice,
  percent: number | null,
  myChoiceId: string | null,
): HTMLElement {
  const isMine = choice.id === myChoiceId;
  const row = createElement("div", "flex flex-col gap-1.5");
  const labels = createElement("div", "flex items-center justify-between gap-2 text-[13px]");
  const answer = createElement("div", "flex min-w-0 items-center gap-1.5");
  answer.append(
    createElement(
      "span",
      `truncate ${isMine ? "font-bold text-gray-900" : "font-normal text-gray-700"}`,
      choice.label,
    ),
  );
  if (isMine) {
    answer.append(
      createElement(
        "span",
        "shrink-0 rounded-full bg-pink-50 px-2 py-0.5 text-[10px] font-bold text-pink-600",
        "あなたの回答",
      ),
    );
  }
  labels.append(answer);
  row.append(labels);
  if (percent === null) {
    return row;
  }
  labels.append(
    createElement(
      "span",
      `shrink-0 font-bold tabular-nums ${isMine ? "text-pink-600" : "text-gray-900"}`,
      `${percent}%`,
    ),
  );

  const track = createElement("div", "h-2 overflow-hidden rounded-full bg-gray-100");
  const bar = createElement("div", `h-full rounded-full ${isMine ? "bg-pink-600" : "bg-gray-300"}`);
  bar.style.width = `${percent}%`;
  track.append(bar);
  row.append(track);
  return row;
}

/** results と closed が同じ集計表示を使い、割合とバーの表現が食い違わないようにする。 */
function createResultList(
  poll: Poll,
  results: PollResults,
  myChoiceId: string | null,
): HTMLElement {
  const resultList = createElement("div", "flex flex-col gap-3.5");
  for (const choice of poll.choices) {
    const percent =
      results.kind === "collecting" ? null : getChoicePercentage(results.percentages, choice.id);
    resultList.append(createResultRow(choice, percent, myChoiceId));
  }
  return resultList;
}

/** 回答済み状態を、最新集計と回答変更導線を含むカードとして描く。 */
function renderResults(
  state: Extract<PollFlowState, { kind: "results" }>,
  flow: PollFlow,
): HTMLElement {
  const card = createCard();
  const status = createElement("div", "flex items-center gap-2");
  status.append(
    createAnsweredBadge(),
    // 集計中は「みんなの回答結果」が無いため、見出しを灰色の状態ピルにして状態を示す。
    state.results.kind === "collecting"
      ? createFocusHeading("初期集計中", STATUS_PILL_CLASS_NAME, RESULT_ANNOUNCEMENT_ID)
      : createFocusHeading("みんなの回答結果", "text-xs text-gray-500", RESULT_ANNOUNCEMENT_ID),
  );
  card.append(createHeader(), createQuestion(state.poll), status);

  const footer = createElement(
    "div",
    "flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-3",
  );
  if (state.results.kind === "full") {
    footer.append(createResponseCount(state.results.total));
  }
  if (state.poll.availability === "open") {
    const changeButton = createElement(
      "button",
      "text-xs font-semibold text-pink-600 hover:text-pink-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600",
      "回答を変更する",
    );
    changeButton.type = "button";
    changeButton.addEventListener("click", () => flow.changeAnswer());
    footer.append(changeButton);
  } else {
    // 回答済みでも受付終了後は失敗する変更操作を出さず、終了理由を明示する。
    footer.append(
      createElement(
        "span",
        "text-xs font-medium text-gray-600",
        state.poll.availability === "period_ended"
          ? "投票期間終了"
          : "この投票は受付を終了しました",
      ),
    );
  }
  card.append(createResultList(state.poll, state.results, state.myChoiceId), footer);
  return card;
}

/** デザイン原本と同じ脈動を持つ、内容を推測させない矩形スケルトンを生成する。 */
function createSkeleton(className: string): HTMLElement {
  return createElement("div", `${className} animate-[pulse_1.6s_ease-in-out_infinite]`);
}

/** 初回通信中にレイアウト高を確保し、読み込み状態を支援技術にも通知する。 */
function renderLoading(): HTMLElement {
  const card = createCard();
  card.setAttribute("role", "status");
  card.setAttribute("aria-live", "polite");
  const choices = createElement("div", "flex flex-col gap-2.5");
  for (let index = 0; index < 4; index += 1) {
    choices.append(createSkeleton("h-[46px] rounded-[10px] border border-gray-100 bg-gray-50"));
  }
  card.append(
    createHeader(false),
    createSkeleton("h-4 w-full rounded-md bg-gray-100"),
    createSkeleton("h-4 w-[70%] rounded-md bg-gray-100"),
    choices,
    createSkeleton("h-[46px] rounded-full bg-gray-100"),
    createElement("p", "text-center text-[11px] text-gray-400", "投票を準備しています…"),
  );
  return card;
}

/** 受付終了カードを共用し、開始前だけは無効な選択肢を示して集計・回答者数を隠す。 */
function renderClosed(
  state: Extract<PollFlowState, { kind: "closed" | "scheduled" }>,
  flow: PollFlow,
): HTMLElement {
  const card = createCard();
  const notice =
    state.kind === "scheduled"
      ? "投票期間開始前"
      : state.poll.availability === "period_ended"
        ? "投票期間終了"
        : "受付終了";
  const closedNotice = createElement("div", "flex flex-wrap items-center gap-2");
  closedNotice.append(createElement("span", STATUS_PILL_CLASS_NAME, notice));
  if (notice === "受付終了") {
    closedNotice.append(
      createElement("span", "text-xs text-gray-500", "この投票は受付を終了しました"),
    );
  }
  card.append(createHeader(), createQuestion(state.poll, RESULT_ANNOUNCEMENT_ID), closedNotice);
  if (state.kind === "scheduled") {
    const choices = createElement("fieldset", "flex min-w-0 flex-col gap-2.5");
    choices.append(createElement("legend", "sr-only", "開始前の選択肢（選択不可）"));
    for (const choice of state.poll.choices) {
      choices.append(createChoiceLabel(choice, null, "scheduled", flow));
    }
    card.append(choices);
    return card;
  }
  card.append(createResultList(state.poll, state.results, null));
  // 人数は full のときだけフッターに出す。percentages は割合の一覧で完結するため、フッターを置かない。
  if (state.results.kind === "full") {
    const footer = createElement("div", "border-t border-gray-100 pt-3");
    footer.append(createResponseCount(state.results.total));
    card.append(footer);
  }
  return card;
}

/** 保存拒否の理由を短く表示し、待たずに結果へ進める操作を既存カード内に置く。 */
function renderRejected(flow: PollFlow): HTMLElement {
  const card = createCard();
  const body = createElement("div", "flex flex-col items-center gap-2 py-4 text-center");
  body.append(
    createErrorIcon(),
    createFocusHeading(
      "受付期間外です",
      "text-sm font-semibold text-gray-700",
      RESULT_ANNOUNCEMENT_ID,
    ),
  );
  const button = createElement(
    "button",
    "flex h-11 w-full items-center justify-center rounded-full border border-pink-600 bg-white px-6 text-sm font-bold text-pink-600 hover:bg-pink-50 hover:text-pink-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600",
    "結果を見る",
  );
  button.type = "button";
  button.addEventListener("click", () => void flow.showLatestAfterRejection());
  card.append(createHeader(false), body, button);
  return card;
}

/** エラーコードを、読者向け文言と再試行可否へ一か所で対応付ける。 */
export function getErrorPresentation(code: Extract<PollFlowState, { kind: "error" }>["code"]): {
  title: string;
  description: string;
  retryable: boolean;
} {
  switch (code) {
    case "internal_error":
    case "service_busy":
    case "unknown":
      return {
        title: "投票を読み込めませんでした",
        description: "通信状況をご確認のうえ、もう一度お試しください",
        retryable: true,
      };
    case "site_or_origin_not_allowed":
    case "origin_required":
      return {
        title: "この記事では投票を利用できません",
        description: "記事の設定をご確認ください",
        retryable: false,
      };
    case "invalid_request":
    case "poll_not_found":
    case "poll_not_open":
    case "visitor_required":
      return {
        title: "投票を表示できませんでした",
        description: "現在、この投票は表示できません",
        retryable: false,
      };
  }
}

/** エラーを回復可能性に応じて描き分け、可能な場合だけ再試行導線を出す。 */
function renderError(
  state: Extract<PollFlowState, { kind: "error" }>,
  flow: PollFlow,
): HTMLElement {
  const card = createCard();
  card.setAttribute("role", "alert");
  const presentation = getErrorPresentation(state.code);
  const body = createElement("div", "flex flex-col items-center gap-2 py-4 text-center");
  body.append(
    createErrorIcon(),
    createFocusHeading(presentation.title, "text-sm font-semibold text-gray-700"),
    createElement("p", "text-xs text-gray-500", presentation.description),
  );
  card.append(createHeader(false), body);
  if (presentation.retryable) {
    const retryButton = createElement(
      "button",
      "flex h-11 w-full items-center justify-center rounded-full border border-pink-600 bg-white px-6 text-sm font-bold text-pink-600 hover:bg-pink-50 hover:text-pink-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600",
      "再試行",
    );
    retryButton.type = "button";
    retryButton.addEventListener("click", () => void flow.retry());
    card.append(retryButton);
  }
  return card;
}

/** 状態の判別だけを行い、各状態専用の描画関数へ委譲する。 */
function renderState(state: PollFlowState, flow: PollFlow): HTMLElement {
  switch (state.kind) {
    case "loading":
      return renderLoading();
    case "select":
    case "submitting":
      return renderSelection(state, flow);
    case "results":
      return renderResults(state, flow);
    case "closed":
    case "scheduled":
      return renderClosed(state, flow);
    case "rejected":
      return renderRejected(flow);
    case "error":
      return renderError(state, flow);
  }
}

/**
 * 投票枠へShadow DOMを作り、core状態を描画して操作をcoreメソッドへ戻す。
 * 戻り値は動的なアンマウントが必要になった場合に使える購読解除関数。
 */
export function mountPollWidget(
  container: HTMLElement,
  flow: PollFlow,
  widgetStyles: string,
): () => void {
  const shadow = createShadowRoot(container, widgetStyles);
  if (shadow === null) {
    return () => undefined;
  }
  const root = createElement("div", "text-gray-900");
  // カード差し替えの外に置き、VoiceOverが内容更新を確実に検出できる状態を保つ。
  const liveRegion = createElement("div", "sr-only");
  liveRegion.id = RESULT_ANNOUNCEMENT_ID;
  liveRegion.setAttribute("role", "status");
  liveRegion.setAttribute("aria-live", "polite");
  liveRegion.setAttribute("aria-atomic", "true");
  shadow.append(root, liveRegion);
  // 非同期遷移の途中で操作要素が消えても、完了画面へフォーカスを引き継ぐ。
  let shouldRestoreWidgetFocus = false;
  let previousKind: PollFlowState["kind"] | undefined;
  // カード購読の寿命に紐づけ、拒否画面を離れた時点とアンマウント時にタイマーを破棄する。
  let rejectionTimer: ReturnType<typeof setTimeout> | undefined;

  const unsubscribe = flow.subscribe((state) => {
    if (rejectionTimer !== undefined) {
      clearTimeout(rejectionTimer);
      rejectionTimer = undefined;
    }
    // 描画関数を純粋に保ち、同じ状態の再描画で警告が重複しないよう購読境界で一度だけ記録する。
    if (state.kind === "error") {
      console.warn(`[vote-sdk] ${state.code}: ${state.message}`);
    }
    // ラジオ・ボタンから始まった遷移だけを追跡し、初回表示時にはフォーカスを奪わない。
    shouldRestoreWidgetFocus ||= shadow.activeElement !== null;
    root.replaceChildren(renderState(state, flow));
    // VoiceOverがlive更新を読み飛ばしても、直後にフォーカスする見出しの説明として結果全文を読めるよう先に反映する。
    liveRegion.textContent = getStateAnnouncement(state, previousKind);
    if (state.kind === "select" && shouldRestoreWidgetFocus) {
      const selectedRadio = Array.from(
        root.querySelectorAll<HTMLInputElement>('input[type="radio"]'),
      ).find((radio) => radio.value === state.selectedChoiceId);
      (selectedRadio ?? root.querySelector<HTMLInputElement>('input[type="radio"]'))?.focus();
      shouldRestoreWidgetFocus = false;
    } else if (
      shouldRestoreWidgetFocus &&
      (state.kind === "results" ||
        state.kind === "closed" ||
        state.kind === "scheduled" ||
        state.kind === "rejected" ||
        state.kind === "error")
    ) {
      root.querySelector<HTMLElement>("[data-vote-focus-target]")?.focus();
      shouldRestoreWidgetFocus = false;
    }
    previousKind = state.kind;
    if (state.kind === "rejected") {
      rejectionTimer = setTimeout(() => void flow.showLatestAfterRejection(), 3_000);
    }
  });
  return () => {
    if (rejectionTimer !== undefined) clearTimeout(rejectionTimer);
    unsubscribe();
  };
}
